import {
  PText,
  PHeadline,
  PTextList,
  PTextListItem,
  PGrid,
  PGridItem,
  PFlexItem,
  PInlineNotification
} from '@porsche-design-system/components-react'
import clsx from 'clsx'
import parse from 'html-react-parser'
import React from 'react'
import { LightgalleryItem } from 'react-lightgallery'

import apiguide from '../../shared/content/apiguide.json'

import Content from '../../shared/components/Content/Content'
import ContentHeader from '../../shared/components/Content/Header/ContentHeader'
import ContentMain from '../../shared/components/Content/Main/ContentMain'
import ContentSection from '../../shared/components/Content/Main/Section/ContentSection'

import styles from './integrationsguide.module.css'

const ENV_LABEL = process.env.REACT_APP_ENV_LABEL || ''

type Section = {
  imageSrc?: string
  imageAlt?: string
  headline?: string
  subHeadline?: string
  textDate?: string
  text?: string
  list?: string[]
  subHeadlineLeft?: string
  textLeft?: string
  listLeft?: string[]
  subHeadlineRight?: string
  textRight?: string
  listRight?: string[]
  imageLeftSrc?: string
}

type Props = {
  type: 'api'
}

const IntegrationsGuide: React.FC<Props> = ({ type }) => {
  const getContent = () => {
    switch (type) {
      case 'api':
        return {
          title: apiguide.apititle,
          sections: apiguide.apisections
        }
    }
  }

  const { title, sections } = getContent()

  function getBoldStyle(section: any) {
    for (const [key] of Object.entries(section)) {
      if (typeof section[key] === 'string' || section[key] instanceof String) {
        const elements = section[key]?.match(/(\*\*)(.*?)(\*\*)/gm)

        if (elements) {
          elements.map((element: any) => {
            const boldElement = '<strong>' + element.substring(2, element.length - 2) + '</strong>'
            section[key] = section[key]?.replace(element, boldElement)
          })
        }
      }

      if (section[key] instanceof Array) {
        const subElements = section[key]?.map((subelement: any) => {
          if (subelement.match(/(\*\*)(.*?)(\*\*)/gm) === null) {
            return subelement
          }
          const newElement = subelement.match(/(\*\*)(.*?)(\*\*)/gm)
          for (let i = 0; i < newElement.length; i++) {
            const boldElement = '<strong>' + newElement[i].substring(2, newElement[i].length - 2) + '</strong>'
            subelement = subelement?.replace(newElement[i], boldElement)
          }
          return subelement
        })
        section[key] = subElements
      }
    }
  }

  sections.map((section: any) => {
    getBoldStyle(section)
  })

  return (
    <Content>
      <ContentHeader>
        <PFlexItem>
          <PText size='small'>How-To / {title}</PText>
        </PFlexItem>
      </ContentHeader>
      <ContentMain>
        {ENV_LABEL && (
          <PInlineNotification heading={ENV_LABEL} state='warning' persistent>
            Alle Angaben in diesem Teil beziehen sich auf die produktive Umgebung.
          </PInlineNotification>
        )}
        {sections.map((section: Section, i: number) => {
          const showGrid =
            section.textLeft ||
            section.subHeadlineLeft ||
            section.listLeft ||
            section.textRight ||
            section.subHeadlineRight ||
            section.listRight

          const textWithoutHeadlineLeft =
            !section.subHeadlineLeft && section.subHeadlineRight ? styles.textWithoutHeadlineLeft : ' '

          const textWithoutHeadlineRight =
            section.subHeadlineLeft && !section.subHeadlineRight ? styles.textWithoutHeadlineRight : ' '

          return (
            <ContentSection key={i} imageSrc={section.imageSrc} imageAlt={section.imageAlt}>
              <div className={styles.content}>
                {section.headline && <PHeadline variant='headline-4'>{parse(section.headline)}</PHeadline>}
                {section.subHeadline && <PHeadline variant='headline-5'>{parse(section.subHeadline)}</PHeadline>}
                {section.textDate && (
                  <PText size='x-small' color='neutral-contrast-medium' className={styles.date}>
                    {parse(section.textDate)}
                  </PText>
                )}
                {section.text && <PText className={styles.text}>{parse(section.text)}</PText>}

                {section.list && (
                  <PTextList>
                    {section.list?.map((element, i) => (
                      <PTextListItem key={i}>{parse(element)}</PTextListItem>
                    ))}
                  </PTextList>
                )}

                {showGrid && (
                  <PGrid>
                    <PGridItem size={{ base: 12, m: 6 }}>
                      {section.subHeadlineLeft && (
                        <PHeadline variant='headline-5'>{parse(section.subHeadlineLeft)}</PHeadline>
                      )}
                      {section.textLeft && (
                        <PText className={clsx(styles.text, textWithoutHeadlineLeft)}>{parse(section.textLeft)}</PText>
                      )}
                      {section.listLeft && (
                        <PTextList className={textWithoutHeadlineLeft}>
                          {section.listLeft?.map((element, i) => (
                            <PTextListItem key={i}>{parse(element)}</PTextListItem>
                          ))}
                        </PTextList>
                      )}
                      {section.imageLeftSrc && (
                        <LightgalleryItem src={section.imageLeftSrc}>
                          <img src={section.imageLeftSrc} className={styles.smallImage} alt='screenshot' />
                        </LightgalleryItem>
                      )}
                    </PGridItem>
                    <PGridItem size={{ base: 12, m: 6 }}>
                      {section.subHeadlineRight && (
                        <PHeadline variant='headline-5'>{parse(section.subHeadlineRight)}</PHeadline>
                      )}
                      {section.textRight && (
                        <PText className={clsx(styles.text, textWithoutHeadlineRight)}>
                          {parse(section.textRight)}
                        </PText>
                      )}

                      {section.listRight && (
                        <PTextList className={textWithoutHeadlineRight}>
                          {section.listRight?.map((element, i) => (
                            <PTextListItem key={i}>{parse(element)}</PTextListItem>
                          ))}
                        </PTextList>
                      )}
                    </PGridItem>
                  </PGrid>
                )}
              </div>
            </ContentSection>
          )
        })}
      </ContentMain>
    </Content>
  )
}

export default IntegrationsGuide
